import {Box, Typography} from '@mui/material';
import {t} from 'i18next';
import React, {useState} from 'react';
import {useNavigate} from 'react-router-dom';
import ArrButton from '../../../common/arr-button/ArrButton';
import ArrModal from '../../../common/arr-modal/ArrModal';
import ArrDataGrid from '../../../common/arr-data-grid/ArrDataGrid';
import {GridColDef} from '@mui/x-data-grid';
import {
  usePostIngestionPreview,
  usePostIngestionRun,
} from '../../../../hooks/ingestions/useIngestions';
import {
  IIngestionPreviewPayload,
  IIngestionPreviewResponse,
  IMappingResponse,
} from '../../../../api/types/ingestionApi.type';
import {ReactComponent as InfoIcon} from '../../../../assets/images/info-icon.svg';
import {FormValues} from '../schema';
import {GridFormValues} from '../gridSchema';
import {UseFormGetValues} from 'react-hook-form';
import ArrToastNotification from '../../../common/arr-toast-notification/ArrToastNotification';
import {ToastType} from '../../../../shared/constants';
import {transformPreviewData} from '../../../../utils/previewDataTransformer';
import ArrTooltipWrapper from '../../../common/arr-tooltip-wrapper/ArrTooltipWrapper';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';

// Shared mapping utilities (refactored)
import {
  validateMandatoryMappings,
  hasIncompleteNonMandatoryRows,
  buildIngestionMappingModels,
  getDuplicateDestinationNames,
} from '../utils/ingestionMappingUtils';

interface CfmHeaderProps {
  rightPaneEnabled: boolean;
  onBack: () => void;
  formValues: FormValues;
  mappingData: IMappingResponse | null;
  lstServerLocation: Array<{value: string; text: string}>;
  gridGetValues: UseFormGetValues<GridFormValues>;
  ingestionId: number | null;
  onSaveDraft: () => Promise<number | null>;
}

/**
 * CfmHeaderTop renders only the heading, info icon, and description.
 * This is placed before the grid in DOM order (items 15-16).
 */
export const CfmHeaderTop: React.FC<{rightPaneEnabled: boolean}> = ({
  rightPaneEnabled: _rightPaneEnabled,
}) => {
  return (
    <>
      {/* 15. Configure Field Mapping Section Heading + 16. Info Icon */}
      <Box
        className="ingestion-field-mapping"
        role="region"
        aria-labelledby="cfm-section-title"
      >
        <Box className="selection-title">
          <Typography
            id="cfm-section-title"
            className="selection-heading"
            tabIndex={0}
            component="h2"
            data-testid={`cfm-header-title`}
          >
            {t('ingestions.Wizard.cfm.title')}
          </Typography>
          <ArrTooltipWrapper
            title={t('ingestions.Wizard.cfm.subtitle')}
            closeTooltipOnEscPress
            triggerKeyDownOnTab
            trigger={
              <InfoIcon
                fontSize="medium"
                className="view-field-mapping-info-icon"
                tabIndex={0}
                role="button"
                data-testId="field-mapping-info-icon"
                aria-label={t(
                  'ingestions.Wizard.ariaLabels.cfmHeader.infoIcon'
                )}
                onKeyDown={(e) => {
                  if (!_rightPaneEnabled) {
                    return;
                  }
                  if (e.key === 'Tab' && e.shiftKey) return;
                  if (e.key === 'Tab') {
                    e.preventDefault();
                    (
                      document.querySelector(
                        '[data-testid="back-button"]'
                      ) as HTMLElement
                    )?.focus();
                  }
                }}
              />
            }
          >
            <div tabIndex={0}>{t('ingestions.Wizard.cfm.modaldesc')}</div>
            <h3 data-testid={`cfm-header-handling`} tabIndex={0}>
              {t('ingestions.Wizard.cfm.handling')}
            </h3>
            <div tabIndex={0}>{t('ingestions.Wizard.cfm.handlingdesc')}</div>
            <h3 data-testid={`cfm-header-required`} tabIndex={0}>
              {t('ingestions.Wizard.cfm.required')}
            </h3>
            <div tabIndex={0}>{t('ingestions.Wizard.cfm.requireddesc')}</div>
            <h3 data-testid={`cfm-header-workproduct`} tabIndex={0}>
              {t('ingestions.Wizard.cfm.workproduct')}
            </h3>
            <div tabIndex={0}>{t('ingestions.Wizard.cfm.workproductdesc')}</div>
          </ArrTooltipWrapper>
        </Box>
      </Box>
      {/* Description Row */}
      <Box className="view-ingestion-title">
        <Typography
          className="typo-text body2_size view-ingestionSummary-title-text"
          role="note"
          aria-live="polite"
          tabIndex={0}
          onKeyDown={(e: any) => {
            if (!_rightPaneEnabled) {
              return;
            }
            if (e.key === 'Tab' && e.shiftKey) {
              e.preventDefault();
              (
                document.querySelector(
                  '[data-testid="preview-button"]'
                ) as HTMLElement
              )?.focus();
            }
          }}
          id="cfm-description"
          data-testid={`cfm-header-description`}
        >
          {t('ingestions.Wizard.cfm.description')}
        </Typography>
        <Typography
          className="typo-text body2_size text-headings view-ingestionSummary-title-text cfm-denotes-text"
          role="note"
          aria-live="polite"
          tabIndex={0}
          id="cfm-denotes"
          data-testid={`cfm-header-denotes`}
        >
          {t('ingestions.Wizard.cfm.denotes')}
        </Typography>
      </Box>
    </>
  );
};

/**
 * CfmHeaderButtons renders Back and Preview buttons + modals.
 * This is placed AFTER the grid in DOM order for accessibility (items 19-20),
 * but visually positioned at the top using CSS.
 */
const CfmHeader: React.FC<CfmHeaderProps> = ({
  rightPaneEnabled,
  onBack,
  formValues,
  mappingData,
  lstServerLocation,
  gridGetValues,
  onSaveDraft,
}) => {
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [previewColumns, setPreviewColumns] = useState<GridColDef[]>([]);
  const [previewRows, setPreviewRows] = useState<any[]>([]);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [duplicateMappingTexts, setDuplicateMappingTexts] = useState('');

  const isShowingDatErrorGrid =
    (formValues.ingestionType?.key === '2' || formValues.ingestionType?.key === 'Overlay') && !formValues.sources.dat.enabled;

  const [showUnmappedConfirmModal, setShowUnmappedConfirmModal] =
    useState(false);
  const [pendingPreviewPayload, setPendingPreviewPayload] =
    useState<IIngestionPreviewPayload | null>(null);

  const previewMutation = usePostIngestionPreview(
    (data: IIngestionPreviewResponse) => {
      const transformed = transformPreviewData(data.lstFileData);
      setPreviewColumns(transformed.columns as GridColDef[]);

      const limitedRows = transformed.rows.slice(0, 50);

      setPreviewRows(limitedRows);
      setShowModal(true);
    },
    () => {
      setValidationError('Failed to load preview data');
    }
  );

  const runIngestionMutation = usePostIngestionRun(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    (data) => {
      setShowModal(false);
      navigate('/Ingestion/Home');
    },
    (error) => {
      setValidationError(
        error.message || 'Failed to run ingestion. Please try again.'
      );
    }
  );

  const buildPreviewPayload = (
    gridValues: GridFormValues
  ): IIngestionPreviewPayload => {
    const ingestionMappingModels = buildIngestionMappingModels(
      gridValues.columnMappings,
      mappingData
    );

    return {
      serverLocation: formValues.serverLocation?.key ?? '',
      folderName:
        formValues.sourceFolder?.displayValue ??
        formValues.sourceFolder?.key ??
        '',
      selectedFile: formValues.sources.dat.loadFile?.key ?? '',
      ingestionMappingModels,
      fieldSeparator: Number(formValues.fieldSeparator?.key ?? 0),
      textSeparator: Number(formValues.textQualifier?.key ?? 0),
      newLineSeparator: Number(formValues.multiValue?.key ?? 0),
      rowCount: mappingData?.rowCount ?? 0,
      serverLocationDetails: lstServerLocation,
    };
  };

  const handlePreview = () => {
    setValidationError(null);
    if (isShowingDatErrorGrid) {
      setShowUnmappedConfirmModal(true);
      return;
    }
    const gridValues = gridGetValues();
    const duplicateTexts = getDuplicateDestinationNames(
      gridValues.columnMappings
    );
    if (duplicateTexts.length > 0) {
      setDuplicateMappingTexts(duplicateTexts.join(', '));
      return;
    }
    const mandatoryValidation = validateMandatoryMappings(
      gridValues.columnMappings
    );
    if (!mandatoryValidation.ok) {
      setValidationError(mandatoryValidation.message);
      return;
    }

    if (hasIncompleteNonMandatoryRows(gridValues.columnMappings)) {
      const payload = buildPreviewPayload(gridValues);
      setPendingPreviewPayload(payload);
      setShowUnmappedConfirmModal(true);
      return;
    }

    const payload = buildPreviewPayload(gridValues);
    previewMutation.mutate(payload);
  };

  const handleUnmappedConfirmYes = () => {
    setShowUnmappedConfirmModal(false);
    if (isShowingDatErrorGrid) {
      setShowModal(true);
      return;
    }
    if (pendingPreviewPayload) {
      previewMutation.mutate(pendingPreviewPayload);
      setPendingPreviewPayload(null);
    }
  };

  const handleUnmappedConfirmNo = () => {
    setShowUnmappedConfirmModal(false);
    setPendingPreviewPayload(null);

    setTimeout(() => {
      (
        document.querySelector('[data-testid="preview-button"]') as HTMLElement
      )?.focus();
    }, 0);
  };

  const handleRunIngestion = async () => {
    try {
      const currentIngestionId = await onSaveDraft();

      if (!currentIngestionId) {
        setValidationError(
          'Failed to save draft. Please try saving manually before running ingestion.'
        );
        return;
      }

      setValidationError(null);

      runIngestionMutation.mutate(currentIngestionId);
    } catch {
      setValidationError('Failed to prepare ingestion. Please try again.');
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setTimeout(() => {
      (
        document.querySelector('[data-testid="preview-button"]') as HTMLElement
      )?.focus();
    }, 0);
  };

  return (
    <>
      {/* 19. Back Button + 20. Preview & Run Button — in DOM after grid for a11y, visually at top via CSS */}
      <Box
        className="cfm-action-buttons-container"
        role="navigation"
        aria-label={
          t('ingestions.Wizard.ariaLabels.cfmHeader.actionButtons') ||
          'Field mapping actions'
        }
      >
        <ArrButton
          variant="secondary"
          startIcon={<ArrowBackIosIcon />}
          type="button"
          size="small"
          disabled={!rightPaneEnabled}
          onClick={onBack}
          onKeyDown={(e) => {
            if (e.key === 'Tab' && e.shiftKey) {
              e.preventDefault();
              (
                document.querySelector(
                  '[data-testid="field-mapping-info-icon"]'
                ) as HTMLElement
              )?.focus();
            }
          }}
          tabIndex={0}
          aria-label={t('ingestions.Wizard.ariaLabels.cfmHeader.backButton')}
          dataTestId="back-button"
        >
          {t('common.back')}
        </ArrButton>
        <ArrButton
          variant="primary"
          endIcon={<ArrowForwardIosIcon />}
          type="button"
          size="small"
          disabled={!rightPaneEnabled || previewMutation.isPending}
          onClick={handlePreview}
          onKeyDown={(e) => {
            if (e.key === 'Tab' && e.shiftKey) {
              return;
            }
            if (e.key === 'Tab') {
              e.preventDefault();
              (
                document.getElementById('cfm-description') as HTMLElement
              )?.focus();
            }
          }}
          tabIndex={0}
          aria-label={t('ingestions.Wizard.ariaLabels.cfmHeader.previewButton')}
          loading={previewMutation.isPending}
          dataTestId="preview-button"
        >
          {previewMutation.isPending
            ? 'Loading...'
            : t('ingestions.Wizard.cfm.preview')}
        </ArrButton>
      </Box>
      {/* Unmapped Rows Confirmation Modal */}
      {showUnmappedConfirmModal && (
        <ArrModal
          showModal={showUnmappedConfirmModal}
          onClose={handleUnmappedConfirmNo}
          title="Confirm"
          description={
            <div tabIndex={0}>
              You have several unmapped rows, which will be ignored and
              discarded. You can add additional rows for mapping by clicking the
              Add button, if needed. Do you want to continue?
            </div>
          }
          ariaDescription="Confirm whether to proceed with unmapped rows"
          autoFocusCloseButton={true}
          dataTestId="unmapped-confirm-modal"
          buttons={[
            {
              id: '1',
              label: 'No',
              onClick: handleUnmappedConfirmNo,
              variant: 'secondary',
              dataTestId: 'umc-no-btn',
            },
            {
              id: '2',
              label: 'Yes',
              onClick: handleUnmappedConfirmYes,
              variant: 'primary',
              dataTestId: 'umc-yes-btn',
            },
          ]}
        />
      )}
      {/* Preview Modal */}
      <ArrModal
        showModal={showModal}
        onClose={handleCloseModal}
        titleEndIcon={
          <ArrTooltipWrapper
            title={t('ingestions.Wizard.cfm.previewModal.title')}
            closeTooltipOnEscPress
            autoFocusCloseButton
            disablePortal
            dialogClassName="ingestion-wizard-preview-dialog"
            trigger={
              <InfoIcon
                fontSize="medium"
                className="preview-info-icon"
                aria-label={t(
                  'ingestions.Wizard.ariaLabels.cfmHeader.previewInfoIcon'
                )}
                tabIndex={0}
              />
            }
          >
            <p tabIndex={0} data-testid={`cfm-header-desc1`}>
              {t('ingestions.Wizard.cfm.previewModal.desc1')}
            </p>
            <p tabIndex={0} data-testid={`cfm-header-desc2`}>
              {t('ingestions.Wizard.cfm.previewModal.desc2')}
            </p>
          </ArrTooltipWrapper>
        }
        title={t('ingestions.Wizard.cfm.previewModal.modalTitle')}
        className="ingestion-preview-modal"
        showClose
        dataTestId="ingestion-preview-modal"
        buttons={[
          {
            id: '1',
            label: 'Go Back',
            onClick: handleCloseModal,
            variant: 'secondary',
            disabled: runIngestionMutation.isPending,
            size: 'small',
            className: 'ingestion-preview-modal-button',
            dataTestId: 'ip-goback-btn',
            ariaLabel: 'close modal, go back to ingestion wizard',
          },
          {
            id: '2',
            label: runIngestionMutation.isPending
              ? 'Running...'
              : 'Run Ingestion',
            onClick: handleRunIngestion,
            variant: 'primary',
            disabled: runIngestionMutation.isPending,
            size: 'small',
            className: 'ingestion-preview-modal-button',
            dataTestId: 'ip-ingest-btn',
            ariaLabel: 'close modal, run ingestion',
          },
        ]}
      >
        <Box className="preview-grid-wrapper">
          {previewColumns.length > 0 && previewRows.length > 0 ? (
            <ArrDataGrid
              rows={previewRows.map(({id: _id, ...rest}, index) => ({
                id: index,
                ...rest,
              }))}
              columns={previewColumns
                .filter((col) => col.field !== 'id')
                .map((col) => {
                  return {
                    ...col,
                    renderHeader: (params) => {
                      return (
                        <span tabIndex={0} role="gridcell">
                          {params.colDef.headerName}
                        </span>
                      );
                    },
                    renderCell: (params) => {
                      const isDisabled = !params?.value;
                      return (
                        <span tabIndex={isDisabled ? -1 : 0} role="gridcell">
                          {params.value}
                        </span>
                      );
                    },
                  };
                })}
              currentPage={0}
            />
          ) : (
            <></>
          )}
        </Box>
      </ArrModal>
      {/* Validation Error Toast */}
      {validationError && (
        <ArrToastNotification
          message={validationError}
          onClose={() => setValidationError(null)}
          status={[ToastType.Error]}
        />
      )}
      {duplicateMappingTexts && (
        <ArrToastNotification
          message={t('ingestions.Wizard.cfm.duplicateMappingError', {
            fieldName: duplicateMappingTexts,
          })}
          onClose={() => setDuplicateMappingTexts('')}
          status={[ToastType.Error]}
        />
      )}
    </>
  );
};

export default CfmHeader;
